// Search utility functions for enhanced search functionality

/**
 * Calculate Levenshtein distance between two strings
 * Used for fuzzy search and typo tolerance
 */
export function levenshteinDistance(str1: string, str2: string): number {
  const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));

  for (let i = 0; i <= str1.length; i++) {
    matrix[0][i] = i;
  }

  for (let j = 0; j <= str2.length; j++) {
    matrix[j][0] = j;
  }

  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1, // deletion
        matrix[j - 1][i] + 1, // insertion
        matrix[j - 1][i - 1] + indicator // substitution
      );
    }
  }

  return matrix[str2.length][str1.length];
}

/**
 * Fuzzy search with typo tolerance
 * Returns true if the search term matches the target with acceptable error rate
 */
export function fuzzySearch(searchTerm: string, target: string, maxDistance: number = 2): boolean {
  if (!searchTerm || !target) return false;

  const searchLower = searchTerm.toLowerCase();
  const targetLower = target.toLowerCase();

  // Exact match
  if (targetLower.includes(searchLower)) return true;

  // Check if any word in the target matches the search term
  const targetWords = targetLower.split(/\s+/);
  for (const word of targetWords) {
    if (word.length >= 3) { // Only check words with 3+ characters
      const distance = levenshteinDistance(searchLower, word);
      const maxAllowedDistance = Math.floor(word.length * 0.3); // 30% error tolerance
      if (distance <= Math.min(maxDistance, maxAllowedDistance)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Calculate search relevance score
 * Higher score = more relevant
 */
export function calculateRelevanceScore(
  item: { title?: string; name?: string; question?: string; description?: string; content?: string; [key: string]: any },
  searchTerm: string
): number {
  const phrase = searchTerm.trim().toLowerCase();
  if (!phrase) return 0;

  const title = (item.title || item.name || item.question || '').toLowerCase();
  const description = (item.description || '').toLowerCase();
  const content = (item.content || '').toLowerCase();

  // Field weights: title beats description beats body.
  const scoreFor = (term: string, weight: number): number => {
    let s = 0;
    if (title.includes(term)) {
      s += 100 * weight;
      // Bonus for exact match at start
      if (title.startsWith(term)) {
        s += 50 * weight;
      }
    }
    if (description.includes(term)) s += 30 * weight;
    if (content.includes(term)) s += 10 * weight;
    if (fuzzySearch(term, title, 1)) s += 20 * weight;
    return s;
  };

  // The adjacent phrase stays the strongest signal. Individual tokens are also
  // scored so a multi-word query ranks by coverage instead of scoring every
  // result 0 and falling back to the caller's original ordering.
  let score = scoreFor(phrase, 1);

  const tokens = phrase.split(/\s+/).filter(Boolean);
  if (tokens.length > 1) {
    for (const token of tokens) {
      score += scoreFor(token, 0.5);
    }
  }

  return score;
}

/**
 * Sort search results by relevance
 */
export function sortByRelevance<T extends { [key: string]: any }>(
  items: T[],
  searchTerm: string
): T[] {
  if (!searchTerm) return items;

  return [...items].sort((a, b) => {
    const scoreA = calculateRelevanceScore(a, searchTerm);
    const scoreB = calculateRelevanceScore(b, searchTerm);
    return scoreB - scoreA; // Higher scores first
  });
}
